import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiPlus, FiLock, FiSun, FiBriefcase, FiArrowRight, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const steps = [
  { id: 'lock', title: '01 Security', icon: FiLock, options: [
    { id: 'l1', name: 'Titan U-Lock', price: 85, level: 'Level 10', img: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=400&q=80' },
    { id: 'l2', name: 'Flex Pro', price: 45, level: 'Level 6', img: 'https://images.unsplash.com/photo-1628151015968-3a4429e9ef04?auto=format&fit=crop&w=400&q=80' }
  ]},
  { id: 'light', title: '02 Visibility', icon: FiSun, options: [
    { id: 'li1', name: 'LumenBlast 1200', price: 65, type: '800lm Front', img: 'https://images.unsplash.com/photo-1532298229144-0ec0c57515c7?auto=format&fit=crop&w=400&q=80' },
    { id: 'li2', name: 'CityBlink Rear', price: 35, type: 'Smart Rear', img: 'https://images.unsplash.com/photo-1485965120184-e220f721d03e?auto=format&fit=crop&w=400&q=80' }
  ]},
  { id: 'bag', title: '03 Utility', icon: FiBriefcase, options: [
    { id: 'b1', name: 'Aero Commute', price: 120, type: '25L Waterproof', img: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=400&q=80' },
    { id: 'b2', name: 'Messenger Dry', price: 95, type: '15L Sling', img: 'https://images.unsplash.com/photo-1547949003-9792a18a2601?auto=format&fit=crop&w=400&q=80' }
  ]}
];

const KitBuilder = ({ onAddKit }) => {
  const [selections, setSelections] = useState({ lock: null, light: null, bag: null });
  const [isDeploying, setIsDeploying] = useState(false);

  const isComplete = selections.lock && selections.light && selections.bag;
  const total = Object.values(selections).reduce((acc, curr) => acc + (curr?.price || 0), 0);
  const savings = total * 0.15;
  const finalPrice = total - savings;

  const handleDeploy = () => {
    setIsDeploying(true);
    setTimeout(() => {
      onAddKit(selections);
      setSelections({ lock: null, light: null, bag: null });
      setIsDeploying(false);
    }, 1000);
  };

  return (
    <section className="py-32 bg-asphalt">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-[2px] w-12 bg-signal"></div>
            <span className="text-signal font-black text-xs tracking-[0.4em] uppercase">System Configuration</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter">
            Build Your <span className="text-stroke">System.</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mt-6">
            Assemble your tactical loadout and unlock 15% system-wide savings. Engineered for the grid.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 mb-16">
          {steps.map((step) => (
            <div key={step.id} className="space-y-6">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <h3 className="text-xl font-black uppercase tracking-widest">{step.title}</h3>
                <SafeIcon icon={step.icon} className="text-gray-500" />
              </div>

              <div className="grid gap-4">
                {step.options.map((opt) => {
                  const active = selections[step.id]?.id === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setSelections(prev => ({ ...prev, [step.id]: opt }))}
                      className={`relative group flex flex-col p-6 border transition-all duration-300 text-left rounded-sm ${
                        active ? 'border-signal bg-signal/5' : 'border-white/5 bg-asphalt-light hover:border-white/20'
                      }`}
                      aria-pressed={active}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="w-16 h-16 bg-black rounded-sm overflow-hidden border border-white/5">
                          <img src={opt.img} alt={opt.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          active ? 'border-signal bg-signal' : 'border-white/20'
                        }`}>
                          {active && <SafeIcon icon={FiCheck} className="text-white text-xs" />}
                        </div>
                      </div>
                      <h4 className="font-black uppercase tracking-wider text-sm">{opt.name}</h4>
                      <p className="text-gray-500 text-[10px] uppercase font-bold mt-1">{opt.level || opt.type}</p>
                      <p className="text-xl font-black mt-4 text-white">${opt.price}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="relative group p-10 bg-white text-asphalt-dark flex flex-col md:flex-row items-center justify-between gap-8 rounded-sm overflow-hidden">
          <div className="absolute top-0 left-0 w-2 h-full bg-signal"></div>
          <div className="absolute inset-0 hazard-pattern opacity-5 pointer-events-none"></div>
          
          <div className="relative z-10">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500">Experience Calculation</span>
            <div className="flex items-baseline gap-4 mt-2">
              <span className="text-5xl font-black">${isComplete ? finalPrice.toFixed(2) : total.toFixed(2)}</span>
              {isComplete && (
                <span className="text-xl line-through text-gray-400 font-bold">${total.toFixed(2)}</span>
              )}
            </div>
            {isComplete ? (
              <motion.p 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-signal font-black text-xs uppercase tracking-widest mt-2 flex items-center gap-2"
              >
                <SafeIcon icon={FiCheck} /> 15% System Discount Applied
              </motion.p>
            ) : (
              <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-2">Activate all 3 modules to unlock savings</p>
            )}
          </div>

          <button 
            disabled={!isComplete || isDeploying}
            onClick={handleDeploy}
            className={`relative z-10 w-full md:w-auto py-5 px-12 font-black uppercase tracking-[0.2em] text-sm transition-all flex items-center justify-center gap-3 ${
              isComplete && !isDeploying ? 'bg-signal text-white hover:scale-105 shadow-xl active:scale-95' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isDeploying ? (
              <>Deploying System <SafeIcon icon={FiLoader} className="animate-spin" /></>
            ) : (
              <>Deploy System <SafeIcon icon={FiArrowRight} /></>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

export default KitBuilder;